import { ImageUploadModal } from "@/components/avatar-upload/ImageUploadModal.tsx";
import { getRandomDefaultEmoji } from "@/components/emoji/emoji-colors.ts";
import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EmojiPickerDropdown } from "@/components/emoji/EmojiPickerDropdown.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import useEmployeeSearchInline, { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useDepartmentSave, useDepartmentsTree } from "@/queries/use-departments.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ApiDepartment, ApiDepartmentSavePayload } from "@/types/department.types.ts";
import { ApiFilePathTypes } from "@/types/file.types.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Avatar,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Input,
  Modal,
  ModalProps,
  Select,
  SelectItem,
  Stack,
  Switch,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface DepartmentEditModalProps extends ModalProps {
  department: ApiDepartment;
}

export const DepartmentEditModal = ({ department, ...props }: DepartmentEditModalProps) => {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const managerSearch = useEmployeeSearchInline();
  const { canCurrentEmployeeEditField } = usePermissions();
  const { data: employee } = useEmployeeMe();
  const modalsService = useModals();

  const canEditPositions = canCurrentEmployeeEditField(
    EmployeeFieldPermission.manage_employee_position
  );

  const departmentSchema = z.object({
    title: z.string().min(1, t("errors:field_required")),
    manager_id: z.number().nullable(),
    parent_id: z.number().nullable(),
    emoji: z.string().nullable(),
    image_file_uuid: z.string().nullable().optional(),
    assign_manager_to_subordinates: z.boolean(),
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(departmentSchema),
    defaultValues: {
      parent_id: department.parent_id || null,
      manager_id: department.manager?.id || null,
      emoji: department.emoji
        ? department.emoji
        : department.image_url
          ? null
          : department.id != 0
            ? null
            : getRandomDefaultEmoji(emojiDefaults.departments),
      image_file_uuid: undefined,
      assign_manager_to_subordinates: false,
    },
  });

  const watchTitle = watch("title");

  const { data: allDepartmentsRaw } = useDepartmentsTree();
  const allDepartments = useMemo(
    () => allDepartmentsRaw?.filter((d) => d.id !== department.id) ?? [],
    [allDepartmentsRaw, department.id]
  );

  const departmentSave = useDepartmentSave(control);
  const onSubmit = handleSubmit(async (schema) => {
    const savePayload: ApiDepartmentSavePayload = {
      title: schema.title,
      manager_id: schema.manager_id,
      parent_id: schema.parent_id,
      emoji: schema.emoji,
      image_file_uuid: schema.image_file_uuid,
      assign_manager_to_subordinates: schema.assign_manager_to_subordinates || false,
    };
    departmentSave.mutate(
      { id: department.id, payload: savePayload },
      {
        onSuccess: (data) => {
          if (department.id == 0) {
            analyticsService.trackEvent(analyticEvents.departments.created, {
              [analyticProperties.id]: data.id,
            });
          } else {
            analyticsService.trackEvent(analyticEvents.departments.edited, {
              [analyticProperties.id]: data.id,
            });
          }
          close(id);
        },
      }
    );
  });

  const managerOptions = useMemo(() => {
    if (department.manager) {
      return [
        {
          id: department.manager.id,
          title: renderEmployeeName(department.manager),
        },
      ] as SelectItem[];
    }
    return [];
  }, [department.manager]);

  const [imageUrl, setImageUrl] = useState<string | null>(department.image_url || null);

  const handleImageUpload = () => {
    if (!employee) return;
    modalsService.openModal(ImageUploadModal, {
      modalTitle: t("common:image"),
      path: ApiFilePathTypes.department,
      generatePublicLink: true,
      onSave: async (file: ApiFile) => {
        try {
          setValue("image_file_uuid", file.uuid);
          setValue("emoji", null);
          analyticsService.trackEvent(analyticEvents.departments.edited, {
            [analyticProperties.actionType]: "Image Updated",
          });
          setImageUrl(file.url_permanent || null);
          return true;
        } catch {
          return false;
        }
      },
    });
  };

  const handleEmojiSet = (emoji: string) => {
    if (!employee) return;

    try {
      setValue("image_file_uuid", null);
      setValue("emoji", emoji);
      setImageUrl(null);
      analyticsService.trackEvent(analyticEvents.departments.edited, {
        [analyticProperties.actionType]: "Emoji Updated",
      });
    } catch {
      return;
    }
  };

  const handleImageAndEmojiDelete = () => {
    if (!employee) return;

    try {
      setValue("image_file_uuid", null);
      setValue("emoji", null);
      setImageUrl(null);
      analyticsService.trackEvent(analyticEvents.departments.edited, {
        [analyticProperties.actionType]: "Image And Emoji Deleted",
      });
    } catch {
      return;
    }
  };

  const [isDropdownOpen, setDropdownOpen] = useState(false);

  return (
    <>
      <Modal
        {...props}
        title={t("core:department")}
        withCloser
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button
              type={"submit"}
              size={"lg"}
              isLoading={departmentSave.isPending}
              onClick={onSubmit}
            >
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={onSubmit}>
          <Stack gap={"md"}>
            <Stack justify={"center"} items={"center"}>
              <DropdownMenu
                open={isDropdownOpen}
                closeOnClick={false}
                onOpenChange={setDropdownOpen}
                trigger={
                  employee && (
                    <Box className={"relative w-41 h-41 group cursor-pointer "}>
                      <Avatar
                        url={imageUrl}
                        acronym={watchTitle}
                        emoji={getValues("emoji")}
                        size={"2xl"}
                        disableEmojiDefault={true}
                      />
                      <Box
                        className={
                          "bg-dark/20 opacity-0 group-hover:opacity-100 absolute inset-0 rounded-lg flex items-center justify-center"
                        }
                      >
                        <FontAwesomeIcon
                          icon={"fa-light fa-camera"}
                          className={"text-[40px] text-light "}
                        />
                      </Box>
                    </Box>
                  )
                }
              >
                <DropdownMenuItem
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-image"} />}
                  onClick={() => {
                    handleImageUpload();
                    setDropdownOpen(false);
                  }}
                  title={t("common:upload_new")}
                />
                <EmojiPickerDropdown
                  onEmojiSelect={(emoji) => {
                    handleEmojiSet(emoji);
                    setDropdownOpen(false);
                  }}
                >
                  <DropdownMenuItem
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-smile"} />}
                    title={t("common:choose_emoji")}
                  />
                </EmojiPickerDropdown>
                <DropdownMenuItem
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                  onClick={() => {
                    handleImageAndEmojiDelete();
                    setDropdownOpen(false);
                  }}
                  variant={"menuDanger"}
                  title={t("common:delete")}
                />
              </DropdownMenu>
            </Stack>

            <Stack>
              <Input
                label={t("common:title")}
                required={true}
                autoFocus={true}
                type="text"
                {...register("title", {
                  value: department.title,
                })}
                error={errors.title?.message}
              />
              <Controller
                render={({ field: { value, onChange } }) => (
                  <Select
                    label={t("core:parent_department")}
                    options={allDepartments}
                    emptyTitle={t("common:select")}
                    value={value}
                    onChange={onChange}
                    isClearable={true}
                    error={errors.parent_id?.message}
                  />
                )}
                control={control}
                name={"parent_id"}
              />

              <Controller
                render={({ field: { value, onChange } }) => (
                  <Select
                    label={t("core:manager")}
                    emptyTitle={t("common:select")}
                    value={value}
                    options={managerOptions}
                    searchable
                    showAvatar={true}
                    showCaption={true}
                    onSearchOptions={(text) => managerSearch.search(text)}
                    searchOptions={managerSearch.options}
                    loadOptionsOnOpen
                    onChange={onChange}
                    isClearable={true}
                    error={errors.manager_id?.message}
                  />
                )}
                control={control}
                name={"manager_id"}
              />
            </Stack>
            {canEditPositions && (
              <Stack>
                <Controller
                  render={({ field: { value, onChange } }) => (
                    <Switch
                      checked={value}
                      onChange={(newValue) => {
                        onChange(newValue);
                      }}
                      size={"sm"}
                      title={t("core:assign_manager_to_subordinates_title")}
                      description={t("core:assign_manager_to_subordinates_description")}
                    />
                  )}
                  control={control}
                  name={"assign_manager_to_subordinates"}
                />
              </Stack>
            )}
          </Stack>
        </form>
      </Modal>
    </>
  );
};
